<template>
  <div>
    <Select
      :disabled="disabled"
      :value="modelValue"
      ref="selectRef"
      multiple
      :max-tag-count="1"
      @on-change="handleChangeOption"
      :placeholder="placeholder || '请输入搜索'"
      filterable
      @on-query-change="handleQueryChange"
    >
      <Tree
        ref="treeRef"
        style="padding: 0 20px"
        v-if="filteredTreeData.length"
        :data="filteredTreeData"
        multiple
        @on-check-change="handleChange"
        show-checkbox
        :select-node="false"
        :render="renderContent"
      ></Tree>
      <div v-else style="text-align: center; padding: 20px; color: #999">
        暂无匹配数据
      </div>
    </Select>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'

export default {
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  data() {
    return {
      treeValue: [],
      treeData: [],
      query: ''
    }
  },
  created() {
    this.treeData = cloneDeep(this.data)
  },
  watch: {
    data: {
      handler() {
        this.treeData = cloneDeep(this.data)
        this.$nextTick(() => {
          const objLi = []
          this.treeData.map((i) => {
            // 先处理子节点
            let checkedCount = 0
            const children = i.children || []
            children.map((j) => {
              if (this.modelValue.includes(j.value)) {
                objLi.push(j)
                j.checked = true
                checkedCount++
              } else {
                j.checked = false
              }
              j.indeterminate = false
            })
            // 更新父节点状态
            if (checkedCount === 0) {
              i.checked = false
              i.indeterminate = false
            } else if (checkedCount === children.length) {
              i.checked = true
              i.indeterminate = false
            } else {
              i.checked = false
              i.indeterminate = true
            }
          })
          this.treeData = cloneDeep(this.treeData)
          this.handleChange(objLi)
        })
      },
      deep: true
    },
    modelValue: {
      handler(newVal, oldVal) {
        this.treeValue = newVal
        this.$nextTick(() => {
          const objLi = []
          this.treeData.map((i) => {
            // 先处理子节点
            let checkedCount = 0
            const children = i.children || []
            children.map((j) => {
              if (this.modelValue.includes(j.value)) {
                objLi.push(j)
                j.checked = true
                checkedCount++
              } else {
                j.checked = false
              }
              j.indeterminate = false
            })
            // 更新父节点状态
            if (checkedCount === 0) {
              i.checked = false
              i.indeterminate = false
            } else if (checkedCount === children.length) {
              i.checked = true
              i.indeterminate = false
            } else {
              i.checked = false
              i.indeterminate = true
            }
          })
          this.treeData = cloneDeep(this.treeData)
          this.handleChange(objLi)
        })
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    filteredTreeData() {
      if (!this.query) {
        return this.treeData
      }
      const query = this.query.toLowerCase()
      const result = []
      this.treeData.forEach((parent) => {
        // 检查父节点是否匹配
        const parentMatch =
          parent.label && parent.label.toLowerCase().includes(query)
        // 过滤子节点
        const matchedChildren = (parent.children || []).filter(
          (child) => child.label && child.label.toLowerCase().includes(query)
        )
        if (parentMatch) {
          // 父节点匹配，显示全部子节点
          result.push({ ...parent, expand: true })
        } else if (matchedChildren.length > 0) {
          // 子节点匹配，只显示匹配的子节点
          result.push({
            ...parent,
            children: matchedChildren,
            expand: true
          })
        }
      })
      return result
    }
  },
  methods: {
    handleQueryChange(e) {
      // 阻止 Select 默认过滤行为
      this.$refs.selectRef.filterQueryChange = false
      this.$refs.selectRef.query = ''
      // 监听退格键清空
      this.$refs.selectRef.$el.querySelector('.ivu-select-input').oninput = (
        a
      ) => {
        if (a.inputType === 'deleteContentBackward' && !a.target.value) {
          this.query = ''
        }
      }
      // 保持输入框显示搜索内容
      this.$refs.selectRef.$el.querySelector('.ivu-select-input').value = e
      if (e) {
        this.query = e
      }
    },
    renderContent(h, { root, node, data }) {
      return <div>{data.label}</div>
    },
    handleChangeOption(e) {
      this.query = ''
      this.$emit('update:modelValue', e)
    },
    handleChange(e) {
      const selectedValuesSet = new Set()

      const traverseTree = (nodes) => {
        nodes.forEach((node) => {
          if (node.checked) {
            if (node.children) {
              node.children.forEach((child) => {
                selectedValuesSet.add(child.value)
              })
            } else {
              selectedValuesSet.add(node.value)
            }
          }
          if (node.children) {
            traverseTree(node.children)
          }
        })
      }

      traverseTree(e)

      // 提取目前在搜索结果中【可见】的叶子节点值
      const visibleLeafValues = []
      const getVisibleLeaves = (nodes) => {
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            getVisibleLeaves(node.children)
          } else {
            if (node.value !== undefined) {
              visibleLeafValues.push(node.value)
            }
          }
        })
      }
      getVisibleLeaves(this.filteredTreeData)

      // 当前能够被用户操作的、已被选中的可见节点值
      const selectedVisibleValues = Array.from(selectedValuesSet)

      // 因为过滤导致在视图里【不可见】且【已被选中】的值，这些值不应该被覆盖掉，需要保留
      const hiddenSelectedValues = this.modelValue.filter(
        (val) => !visibleLeafValues.includes(val)
      )

      // 去重，得到最终我们要给 Select 组件的组合值
      const finalSelectedValues = Array.from(
        new Set([...hiddenSelectedValues, ...selectedVisibleValues])
      )

      // 更新Select组件显示的 Tag 标签值
      this.$refs.selectRef.values = finalSelectedValues.map((value) => {
        let label = ''
        const findLabel = (nodes) => {
          for (const node of nodes) {
            if (node.value === value) {
              label = node.label
              return true
            }
            if (node.children && findLabel(node.children)) {
              return true
            }
          }
          return false
        }
        findLabel(this.data)
        return { label, value }
      })

      // 防止 watch: modelValue 触发后再次调用 handleChange 产生的无限循环
      const currentValuesStr = JSON.stringify([...this.modelValue].sort())
      const newValuesStr = JSON.stringify([...finalSelectedValues].sort())
      if (currentValuesStr !== newValuesStr) {
        // 触发外部 update 事件更新 v-model
        this.$emit('update:modelValue', finalSelectedValues)
      }
    }
  }
}
</script>

<style lang="less" scoped>
/deep/.ivu-tree-children {
  width: 100%;
}
</style>
